import { Box, Card, CardActions, CardHeader, CardMedia, IconButton, Tooltip, Typography } from '@mui/material';
import { MoreVert, StarRounded, StarBorderRounded } from '@mui/icons-material';
import React from 'react';
import MenuButton from '../MenuButton/MenuButton';
import { docGeniusPalette } from 'styles/partials/colors';
import { MenuOption } from 'dataTypes/document';
import documentSvg from 'assets/images/NewFileOptionsIcons/document-option-icon.svg';
import contractSvg from 'assets/images/NewFileOptionsIcons/contract-option-icon.svg';
import quoteSvg from 'assets/images/NewFileOptionsIcons/quote-option-icon.svg';
import { getDocumentDate } from 'utils/formatDate';

export interface PreviewCardProps {
  createdAt: string;
  id: number;
  isFavorite: boolean;
  label: string;
  lastModified: string;
  menuItems: MenuOption[];
  preview: string;
  isDefault?: boolean;
  onStarClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onClick?: () => void;
  onDoubleClick?: () => void;
}

export function PreviewCard({
  createdAt,
  label,
  lastModified,
  menuItems,
  preview,
  isFavorite,
  isDefault,
  onStarClick,
  onClick,
  onDoubleClick,
}: PreviewCardProps) {
  const moreIcon = (
    <IconButton>
      <MoreVert sx={{ color: docGeniusPalette.primaryBlue }} />
    </IconButton>
  );

  const starIcon = isFavorite ? <StarRounded color='primary' /> : <StarBorderRounded color='primary' />;
  const isPreviewDummy = preview === documentSvg || preview === contractSvg || preview === quoteSvg;

  return (
    <Card
      {...Card}
      sx={{
        bgcolor: '#E7ECF3',
        border: '1px solid #B7B9C0',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '335px',
        maxHeight: '337px',
        maxWidth: '261px',
        boxShadow: 'none',
        cursor: 'pointer',
        '&:hover': {
          background: '#CAD6E9',
          boxShadow: '4px 4px 8px 0px rgba(0, 0, 0, 0.25)',
        },
      }}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      <CardHeader
        {...CardHeader}
        sx={{
          padding: '8px 8px 0px 16px',
        }}
        title={isDefault ? 'Default' : ''}
        action={
          <Box
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <MenuButton
              buttonStyles={{
                borderRadius: '32px',
                minWidth: '40px',
                padding: '0',
              }}
              options={menuItems}
              tooltipLabel='More Options'
            >
              {moreIcon}
            </MenuButton>
          </Box>
        }
      />
      <CardMedia
        {...CardMedia}
        alt={label}
        component='img'
        image={preview}
        sx={{
          marginBottom: '24px',
          marginLeft: '32px',
          marginRight: '32px',
          marginTop: '8px',
          objectFit: 'contain',
          height: isPreviewDummy ? '70px' : '177px',
          width: '199px',
        }}
      />
      <CardActions
        {...CardActions}
        disableSpacing
        sx={{
          bgcolor: 'white',
          borderRadius: '0px 0px 8px 8px',
          justifyContent: 'space-between',
          marginTop: '-3px',
          maxHeight: '76px',
          outline: '1px solid #B7B9C0',
          padding: '16px',
        }}
      >
        <Box display={'flex'} flexDirection={'column'} rowGap={'4px'} alignItems={'flex-start'}>
          <Typography
            component='p'
            sx={{
              color: docGeniusPalette.primaryBlue,
              display: 'block',
              fontFamily: 'Rubik',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              letterSpacing: '0.08px',
              lineHeight: '24px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '183px',
            }}
          >
            {label}
          </Typography>
          <Typography
            component='span'
            sx={{
              color: '#717273',
              fontFamily: 'Rubik',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 400,
              letterSpacing: '0.08px',
              lineHeight: '26px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '183px',
            }}
          >
            {createdAt === lastModified
              ? `Created: ${getDocumentDate(createdAt)}`
              : `Last Modified: ${getDocumentDate(lastModified)}`}
          </Typography>
        </Box>

        <Tooltip title={isFavorite ? 'Remove From Favorites' : 'Add To Favorites'} arrow>
          <IconButton sx={{ padding: '4px' }} aria-label='settings' onClick={onStarClick}>
            {starIcon}
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
}

export default PreviewCard;
